import type {
  HectaresInputGroup,
  InputGroup,
  ModuleSchema,
  T2Schema,
  StartWithWithoutGeneric,
  Submodule,
  StartWithWithoutTierTwo,
  DropdownInputGroup,
  OptionalSubmodule,
  TranslationKey,
  BelongsToLuc,
  DropdownInputGroupTierTwo,
  ForestRadioGroupInputGroup,
} from "@typedefs/modulesInterfaces";
import { FieldType } from "@typedefs/modulesInterfaces";
import * as Yup from "yup";
import type {
  ForestDisturbanceResponse,
  ForestManagementPayload,
  ForestManagementT2Payload,
  ModuleFull,
} from "./forestManagementTypes";
import { getOptions } from "@features/dropdownOptions/fetchOptions";
import { BEModulesClassNames } from "@utils/moduleList";
import {
  createDegradationValidation,
  createLoggingValidation,
  createRotationValidation,
  createdDisturbanceValidation,
} from "./validation";
import {
  standardLucValidation,
  validatePercentage,
  validatePositiveNumber,
} from "../moduleUtils";
import {
  ENTRY_ENDPOINT,
  TAG_ENTRY_DEFAULTS,
} from "@app/features/modules/forestManagementApiSlice";
import { ModuleTypeRegistry } from "@utils/moduleTypeRegistry";

//TODO: work on this, quick solution to handling names in forest, go to commit for more context
export enum ForestManagementSections {
  TYPE_OF_FOREST = "module.type_of_forest",
  ROTATIONS = "module.rotations",
  LOGGING = "module.harvesting",
  DISTURBANCES = "module.disturbances",
  DEGRADATION = "module.aggregate_impact_of_changes_in_forest_conditions",
}

export const includedPropList = [
  "id",
  "status",
  "land_use_change",
  "note",
  "area",
  "forest_condition_type",
  "land_use_type_start",
  "forest_type",
  "rotation_length_yrs_start",
  "rotation_length_yrs_w",
  "rotation_length_yrs_wo",
  "rotation_percentage_biomass_for_energy_start",
  "rotation_percentage_biomass_for_energy_w",
  "rotation_percentage_biomass_for_energy_wo",
  "logging_recurrence_yrs_start",
  "logging_recurrence_yrs_w",
  "logging_recurrence_yrs_wo",
  "logging_percentage_agb_logged_start",
  "logging_percentage_agb_logged_w",
  "logging_percentage_agb_logged_wo",
  "logging_percentage_biomass_for_energy_start",
  "logging_percentage_biomass_for_energy_w",
  "logging_percentage_biomass_for_energy_wo",
  "average_yearly_degradation_percentage_start",
  "average_yearly_degradation_percentage_w",
  "average_yearly_degradation_percentage_wo",
  "land_use_type_thread",
  "rotation_length_yrs_thread",
  "rotation_percentage_biomass_for_energy_thread",
  "logging_recurrence_yrs_thread",
  "logging_percentage_agb_logged_thread",
  "logging_percentage_biomass_for_energy_thread",
  "average_yearly_degradation_percentage_thread",
];
export const includedT2PropList = [
  "soc_t2_start",
  "soc_t2_w",
  "soc_t2_wo",
  "agb_t2_start",
  "agb_t2_w",
  "agb_t2_wo",
  "bgb_t2_start",
  "bgb_t2_w",
  "bgb_t2_wo",
  "litter_t2_start",
  "litter_t2_w",
  "litter_t2_wo",
  "deadwood_t2_start",
  "deadwood_t2_w",
  "deadwood_t2_wo",
  "flu_t2_start",
  "flu_t2_w",
  "flu_t2_wo",
  "fi_t2_start",
  "fi_t2_w",
  "fi_t2_wo",
  "fmg_t2_start",
  "fmg_t2_w",
  "fmg_t2_wo",
  "agb_growth_rate_le_20_yrs_t2_start",
  "agb_growth_rate_le_20_yrs_t2_w",
  "agb_growth_rate_le_20_yrs_t2_wo",
  "agb_growth_rate_gt_20_yrs_t2_start",
  "agb_growth_rate_gt_20_yrs_t2_w",
  "agb_growth_rate_gt_20_yrs_t2_wo",
  "bgb_growth_rate_le_20_yrs_t2_start",
  "bgb_growth_rate_le_20_yrs_t2_w",
  "bgb_growth_rate_le_20_yrs_t2_wo",
  "bgb_growth_rate_gt_20_yrs_t2_start",
  "bgb_growth_rate_gt_20_yrs_t2_w",
  "bgb_growth_rate_gt_20_yrs_t2_wo",
  "agb_max_t2_start",
  "agb_max_t2_w",
  "agb_max_t2_wo",
  "bgb_max_t2_start",
  "bgb_max_t2_w",
  "bgb_max_t2_wo",
  "rotation_start_year_t2_start",
  "rotation_start_year_t2_w",
  "rotation_start_year_t2_wo",
  "logging_start_year_t2_start",
  "logging_start_year_t2_w",
  "logging_start_year_t2_wo",
  "logging_dry_matter_logged_t2_start",
  "logging_dry_matter_logged_t2_w",
  "logging_dry_matter_logged_t2_wo",
  "degradation_dry_matter_impacted_t2_start",
  "degradation_dry_matter_impacted_t2_w",
  "degradation_dry_matter_impacted_t2_wo",
  "data_source",
];

const requiredNumberValidation = (
  message: TranslationKey,
  scenario: "start" | "w" | "wo"
) => {
  return standardLucValidation(message, scenario);
};

type Scenario = "start" | "w" | "wo";

const makeLoggingBiomassSumTest = (scenario: Scenario) => ({
  name: `logging_biomass_sum_${scenario}` as const,
  message: "validations.logging_biomass_sum_exceeded" as TranslationKey,
  test(this: Yup.TestContext, value: number | null | undefined): boolean {
    if (!value) return true;
    const moduleFull = (this as any).from?.[1]?.value as ModuleFull | undefined;
    if (!moduleFull) return true;
    const logging =
      moduleFull.parent[`logging_percentage_agb_logged_${scenario}`] ?? 0;
    const biomassSum = moduleFull.disturbances.reduce(
      (acc: number, d) =>
        acc + ((d[`percentage_biomass_destruction_${scenario}`] as number | null) ?? 0),
      0
    );
    console.log("logging", logging);
    console.log("biomassSum", biomassSum);
    return logging + biomassSum <= 100;
  },
});

export const validationSchema = (belongsToLuc?: BelongsToLuc | undefined) => {
  const rotationValidation = createRotationValidation(belongsToLuc);
  const loggingValidation = createLoggingValidation(belongsToLuc);
  const disturbanceValidation = createdDisturbanceValidation(belongsToLuc);
  const degradationValidation = createDegradationValidation(belongsToLuc);

  const validateFields = (
    fields: any,
    isTriggered: boolean,
    validationFunc: Function,
    prefix: string
  ) => {
    if (isTriggered) {
      fields[`${prefix}_start`] = validationFunc.call(this, "start", prefix);
      fields[`${prefix}_w`] = validationFunc.call(this, "w", prefix);
      fields[`${prefix}_wo`] = validationFunc.call(this, "wo", prefix);
    }
  };

  const getParentValidation = (
    values: ForestManagementPayload,
    isDisturbanceTriggered: boolean
  ) => {
    const fields = {
      rotation_length_yrs_start: validatePositiveNumber,
      rotation_length_yrs_w: validatePositiveNumber,
      rotation_length_yrs_wo: validatePositiveNumber,
      rotation_percentage_biomass_for_energy_start: validatePercentage,
      rotation_percentage_biomass_for_energy_w: validatePercentage,
      rotation_percentage_biomass_for_energy_wo: validatePercentage,
      logging_recurrence_yrs_start: validatePositiveNumber,
      logging_recurrence_yrs_w: validatePositiveNumber,
      logging_recurrence_yrs_wo: validatePositiveNumber,
      logging_percentage_agb_logged_start: validatePositiveNumber,
      logging_percentage_agb_logged_w: validatePercentage,
      logging_percentage_agb_logged_wo: validatePercentage,
      logging_percentage_biomass_for_energy_start: validatePercentage,
      logging_percentage_biomass_for_energy_w: validatePercentage,
      logging_percentage_biomass_for_energy_wo: validatePercentage,
      average_yearly_degradation_percentage_start: validatePercentage,
      average_yearly_degradation_percentage_w: validatePercentage,
      average_yearly_degradation_percentage_wo: validatePercentage,
      forest_type: validatePositiveNumber.required("Forest Type is required"),
      forest_condition_type: validatePositiveNumber.required(
        "Forest Type is required"
      ),
    };

    const isRotationTriggered = Object.keys(values)
      .filter(
        (key) =>
          (key.startsWith("rotation_length_yrs") ||
            key.startsWith("rotation_percentage_biomass_for_energy")) &&
          !key.endsWith("_thread")
      )
      .some((key) => !!values[key as keyof ForestManagementPayload]);
    const isLoggingTriggered = Object.keys(values)
      .filter(
        (key) =>
          (key.startsWith("logging_recurrence_yrs") ||
            key.startsWith("logging_percentage_agb_logged") ||
            key.startsWith("logging_percentage_biomass_for_energy")) &&
          !key.endsWith("_thread")
      )
      .some((key) => !!values[key as keyof ForestManagementPayload]);

    validateFields(
      fields,
      isRotationTriggered,
      rotationValidation,
      "rotation_length_yrs"
    );
    validateFields(
      fields,
      isRotationTriggered,
      rotationValidation,
      "rotation_percentage_biomass_for_energy"
    );
    validateFields(
      fields,
      isLoggingTriggered,
      loggingValidation,
      "logging_recurrence_yrs"
    );
    validateFields(
      fields,
      isLoggingTriggered,
      loggingValidation,
      "logging_percentage_agb_logged"
    );
    validateFields(
      fields,
      isLoggingTriggered,
      loggingValidation,
      "logging_percentage_biomass_for_energy"
    );

    if (
      !isRotationTriggered &&
      !isLoggingTriggered &&
      !isDisturbanceTriggered
    ) {
      fields.average_yearly_degradation_percentage_start =
        degradationValidation.call(
          this,
          "start",
          "average_yearly_degradation_percentage",
          true
        );
      fields.average_yearly_degradation_percentage_w =
        degradationValidation.call(
          this,
          "w",
          "average_yearly_degradation_percentage",
          true
        );
      fields.average_yearly_degradation_percentage_wo =
        degradationValidation.call(
          this,
          "wo",
          "average_yearly_degradation_percentage",
          true
        );
    }

    const mutableFields = fields as Record<string, Yup.NumberSchema>;
    for (const scenario of ["start", "w", "wo"] as const) {
      const key = `logging_percentage_agb_logged_${scenario}`;
      const { name, message, test } = makeLoggingBiomassSumTest(scenario);
      mutableFields[key] = mutableFields[key].test(name, message, test);
    }

    return Yup.object().shape({ ...fields });
  };

  return Yup.lazy((values: ModuleFull) => {
    return Yup.object().shape({
      parent: getParentValidation(
        values.parent,
        values.disturbances.length > 0
      ),
      disturbances: Yup.array().of(
        Yup.object().shape({
          disturbance_type: requiredNumberValidation(
            "validations.disturbance_type_required",
            "start"
          ),
          recurrence_yrs_start: disturbanceValidation.call(
            this,
            "start",
            "recurrence_yrs"
          ),
          recurrence_yrs_w: disturbanceValidation.call(
            this,
            "w",
            "recurrence_yrs"
          ),
          recurrence_yrs_wo: disturbanceValidation.call(
            this,
            "wo",
            "recurrence_yrs"
          ),
          percentage_biomass_destruction_start: disturbanceValidation
            .call(this, "start", "percentage_biomass_destruction", true)
            .test(makeLoggingBiomassSumTest("start")),
          percentage_biomass_destruction_w: disturbanceValidation
            .call(this, "w", "percentage_biomass_destruction", true)
            .test(makeLoggingBiomassSumTest("w")),
          percentage_biomass_destruction_wo: disturbanceValidation
            .call(this, "wo", "percentage_biomass_destruction", true)
            .test(makeLoggingBiomassSumTest("wo")),
        })
      ),
    });
  });
};

export const initialValues: ForestManagementPayload = {
  id: null,
  status: null,
  land_use_change: null,
  note: null,

  area: null,
  forest_type: null,
  forest_condition_type: null,
  land_use_type_start: null,
  rotation_length_yrs_start: null,
  rotation_length_yrs_w: null,
  rotation_length_yrs_wo: null,
  rotation_percentage_biomass_for_energy_start: null,
  rotation_percentage_biomass_for_energy_w: null,
  rotation_percentage_biomass_for_energy_wo: null,
  logging_recurrence_yrs_start: null,
  logging_recurrence_yrs_w: null,
  logging_recurrence_yrs_wo: null,
  logging_percentage_agb_logged_start: null,
  logging_percentage_agb_logged_w: null,
  logging_percentage_agb_logged_wo: null,
  logging_percentage_biomass_for_energy_start: null,
  logging_percentage_biomass_for_energy_w: null,
  logging_percentage_biomass_for_energy_wo: null,
  average_yearly_degradation_percentage_start: null,
  average_yearly_degradation_percentage_w: null,
  average_yearly_degradation_percentage_wo: null,

  activity: null,
  start_year: null,
  soc_t2_start: null,
  soc_t2_w: null,
  soc_t2_wo: null,
  data_source: null,
};

const moduleRegistry = ModuleTypeRegistry.getInstance();

// Lazy evaluation function that gets ID when called
const getForestManagementModuleType = () =>
  moduleRegistry.getIdByClassName(BEModulesClassNames.ForestManagement);

const disturbanceTypes = () => getOptions("disturbanceTypes");
const dataSourceTypes = () => {
  const filterId = getForestManagementModuleType();
  return getOptions("dataSourceTypes", { filterId });
};
const getInitInputGroups: () => InputGroup[] = () => {
  const hectaresInputGroup: HectaresInputGroup = {
    type: FieldType.HECTARES,
    label: "module.hectares",
    unit: "[ha]",
    inputName: "parent.area",
    disabled: true,
  };
  return [hectaresInputGroup];
};

const getMandatorySubmodules: () => Submodule[] = () => {
  const lengthOfRotation: StartWithWithoutGeneric = {
    type: FieldType.SWW,
    inputName: "parent.rotation_length_yrs",
    label: "module.length_of_rotation",
    unit: "[yrs]",
  };
  const biomassPercentage: StartWithWithoutGeneric = {
    type: FieldType.SWW,
    inputName: "parent.rotation_percentage_biomass_for_energy",
    label: "module.percentage_of_harvest_for_energy",
    unit: "[%]",
  };

  const loggingRecurrence: StartWithWithoutGeneric = {
    type: FieldType.SWW,
    inputName: "parent.logging_recurrence_yrs",
    label: "module.harvesting_recurrence",
    unit: "[yrs]",
  };
  const loggingAgb: StartWithWithoutGeneric = {
    type: FieldType.SWW,
    inputName: "parent.logging_percentage_agb_logged",
    label: "module.timber_harvested",
    unit: "[%AGB Logged]",
  };
  const loggingPercentage: StartWithWithoutGeneric = {
    type: FieldType.SWW,
    inputName: "parent.logging_percentage_biomass_for_energy",
    label: "module.biomass_used_for_energy",
    unit: "[%]",
  };

  const degradationPercentage: StartWithWithoutGeneric = {
    type: FieldType.SWW,
    inputName: "parent.average_yearly_degradation_percentage",
    label: "module.avg_yearly_degradation",
    unit: "[%]",
  };

  const forestRadioGroup: ForestRadioGroupInputGroup = {
    type: FieldType.FOREST_RADIO_GROUP,
    label: "module.type_of_forest",
    inputName: "parent.forest_type", // This won't be used directly but kept for consistency
    forestTypeInputName: "parent.forest_type",
    forestConditionTypeInputName: "parent.forest_condition_type",
  };
  const vegetationLevels: DropdownInputGroup = {
    type: FieldType.SELECT,
    label: "module.forest_category",
    unit: "[unit]",
    inputName: "parent.land_use_type_start",
    dropdownOptions: [],
  };

  return [
    {
      name: ForestManagementSections.TYPE_OF_FOREST,
      inputGroups: [forestRadioGroup, vegetationLevels],
    },
    {
      name: ForestManagementSections.ROTATIONS,
      inputGroups: [lengthOfRotation, biomassPercentage],
    },
    {
      name: ForestManagementSections.LOGGING,
      inputGroups: [loggingRecurrence, loggingAgb, loggingPercentage],
    },
    {
      name: ForestManagementSections.DEGRADATION,
      inputGroups: [degradationPercentage],
    },
  ];
};

const getOptionalSubmodules: () => OptionalSubmodule<ForestDisturbanceResponse>[] =
  () => {
    const distType: DropdownInputGroup = {
      type: FieldType.SELECT,
      label: "module.disturbance_type",
      inputName: (index: number) => `disturbances.${index}.disturbance_type`,
      dropdownOptions: disturbanceTypes,
    };
    const distRecurrence: StartWithWithoutGeneric = {
      type: FieldType.SWW,
      label: "module.disturbance_recurrence",
      unit: "[yrs]",
      inputName: (index: number) => `disturbances.${index}.recurrence_yrs`,
    };
    const biomassDistruction: StartWithWithoutGeneric = {
      type: FieldType.SWW,
      label: "module.biomass_destruction",
      unit: "[%]",
      inputName: (index: number) =>
        `disturbances.${index}.percentage_biomass_destruction`,
    };

    return [
      {
        name: "module.disturbance",
        inputName: "module.disturbances",
        inputGroups: [distType, distRecurrence, biomassDistruction],
        repeatable: true,
        linkedTierTwoInputs: ["tiertwo.disturbances"],
        route: ENTRY_ENDPOINT,
        cacheTag: TAG_ENTRY_DEFAULTS,
        max: 3,
      },
    ];
  };

/* T2 */
export const t2InitialValues: ForestManagementT2Payload = {
  soc_t2_start: null,
  soc_t2_w: null,
  soc_t2_wo: null,
  agb_t2_start: null,
  agb_t2_w: null,
  agb_t2_wo: null,
  bgb_t2_start: null,
  bgb_t2_w: null,
  bgb_t2_wo: null,
  agb_max_t2_start: null,
  agb_max_t2_w: null,
  agb_max_t2_wo: null,
  bgb_max_t2_start: null,
  bgb_max_t2_w: null,
  bgb_max_t2_wo: null,
  litter_t2_start: null,
  litter_t2_w: null,
  litter_t2_wo: null,
  deadwood_t2_start: null,
  deadwood_t2_w: null,
  deadwood_t2_wo: null,
  flu_t2_start: null,
  flu_t2_w: null,
  flu_t2_wo: null,
  fi_t2_start: null,
  fi_t2_w: null,
  fi_t2_wo: null,
  fmg_t2_start: null,
  fmg_t2_w: null,
  fmg_t2_wo: null,
  agb_growth_rate_le_20_yrs_t2_start: null,
  agb_growth_rate_le_20_yrs_t2_w: null,
  agb_growth_rate_le_20_yrs_t2_wo: null,
  agb_growth_rate_gt_20_yrs_t2_start: null,
  agb_growth_rate_gt_20_yrs_t2_w: null,
  agb_growth_rate_gt_20_yrs_t2_wo: null,
  bgb_growth_rate_le_20_yrs_t2_start: null,
  bgb_growth_rate_le_20_yrs_t2_w: null,
  bgb_growth_rate_le_20_yrs_t2_wo: null,
  bgb_growth_rate_gt_20_yrs_t2_start: null,
  bgb_growth_rate_gt_20_yrs_t2_w: null,
  bgb_growth_rate_gt_20_yrs_t2_wo: null,
  rotation_start_year_t2_start: null,
  rotation_start_year_t2_w: null,
  rotation_start_year_t2_wo: null,
  logging_start_year_t2_start: null,
  logging_start_year_t2_w: null,
  logging_start_year_t2_wo: null,
  logging_dry_matter_logged_t2_start: null,
  logging_dry_matter_logged_t2_w: null,
  logging_dry_matter_logged_t2_wo: null,
  degradation_dry_matter_impacted_t2_start: null,
  degradation_dry_matter_impacted_t2_w: null,
  degradation_dry_matter_impacted_t2_wo: null,
  data_source: null,
};

const getT2Submodules: (isForestRemainsForest: boolean) => Submodule[] = (
  isForestRemainsForest: boolean
) => {
  const dataSource: DropdownInputGroupTierTwo = {
    type: FieldType.SELECT_T2,
    inputName: "tiertwo.data_source",
    label: "module.forest_data_source",
    dropdownOptions: dataSourceTypes,
    unit: "",
    links: [],
    defaultValue: "",
  };

  const forestManagementRows: StartWithWithoutTierTwo[] = [
    {
      type: FieldType.SWW_T2,
      inputName: "tiertwo.soc_t2",
      label: "module.soil_carbon",
      unit: "[tC/ha]",
      links: [],
      startProps: { inputType: "number" },
      withoutProps: { inputType: "number" },
      withProps: { inputType: "number" },
    },
    {
      type: FieldType.SWW_T2,
      inputName: "tiertwo.flu_t2",
      label: "module.land_use_factor",
      unit: "[#]",
      links: [],
      startProps: { inputType: "number" },
      withoutProps: { inputType: "number" },
      withProps: { inputType: "number" },
    },
    {
      type: FieldType.SWW_T2,
      inputName: "tiertwo.agb_t2",
      label: "module.initial_agb",
      unit: "[tC/ha]",
      links: ["Type of Forest"],
      startProps: { inputType: "number" },
      withoutProps: { inputType: "number", disabled: true },
      withProps: { inputType: "number", disabled: true },
    },
    {
      type: FieldType.SWW_T2,
      inputName: "tiertwo.bgb_t2",
      label: "module.initial_bgb",
      unit: "[tC/ha]",
      links: ["Type of Forest"],
      startProps: { inputType: "number" },
      withoutProps: { inputType: "number", disabled: true },
      withProps: { inputType: "number", disabled: true },
    },
    {
      type: FieldType.SWW_T2,
      inputName: "tiertwo.agb_max_t2",
      label: "module.above_ground_biomass_max",
      unit: "[tC/ha]",
      links: ["Type of Forest"],
      startProps: { inputType: "number", disabled: true },
      withoutProps: { inputType: "number" },
      withProps: { inputType: "number" },
    },
    {
      type: FieldType.SWW_T2,
      inputName: "tiertwo.bgb_max_t2",
      label: "module.below_ground_biomass_max",
      unit: "[tC/ha]",
      links: ["Type of Forest"],
      startProps: { inputType: "number", disabled: true },
      withoutProps: { inputType: "number" },
      withProps: { inputType: "number" },
    },
    {
      type: FieldType.SWW_T2,
      inputName: "tiertwo.agb_growth_rate_le_20_yrs_t2",
      label: "module.agb_growth_rate_under_20",
      unit: "[tC/ha/yr]",
      links: ["Type of Forest"],
      startProps: { inputType: "number", disabled: isForestRemainsForest },
      withoutProps: { inputType: "number", disabled: isForestRemainsForest },
      withProps: { inputType: "number", disabled: isForestRemainsForest },
    },
    {
      type: FieldType.SWW_T2,
      inputName: "tiertwo.agb_growth_rate_gt_20_yrs_t2",
      label: "module.agb_growth_rate_over_20",
      unit: "[tC/ha/yr]",
      links: ["Type of Forest"],
      startProps: { inputType: "number" },
      withoutProps: { inputType: "number" },
      withProps: { inputType: "number" },
    },
    {
      type: FieldType.SWW_T2,
      inputName: "tiertwo.bgb_growth_rate_le_20_yrs_t2",
      label: "module.bgb_growth_rate_under_20",
      unit: "[tC/ha/yr]",
      links: ["Type of Forest"],
      startProps: { inputType: "number", disabled: isForestRemainsForest },
      withoutProps: { inputType: "number", disabled: isForestRemainsForest },
      withProps: { inputType: "number", disabled: isForestRemainsForest },
    },
    {
      type: FieldType.SWW_T2,
      inputName: "tiertwo.bgb_growth_rate_gt_20_yrs_t2",
      label: "module.bgb_growth_rate_over_20",
      unit: "[tC/ha/yr]",
      links: ["Type of Forest"],
      startProps: { inputType: "number" },
      withoutProps: { inputType: "number" },
      withProps: { inputType: "number" },
    },
    {
      type: FieldType.SWW_T2,
      inputName: "tiertwo.litter_t2",
      label: "module.litter",
      unit: "[tC/ha]",
      links: ["Type of Forest"],
      startProps: { inputType: "number" },
      withoutProps: { inputType: "number" },
      withProps: { inputType: "number" },
    },
    {
      type: FieldType.SWW_T2,
      inputName: "tiertwo.deadwood_t2",
      label: "module.dead_wood",
      unit: "[tC/ha]",
      links: ["Type of Forest"],
      startProps: { inputType: "number" },
      withoutProps: { inputType: "number" },
      withProps: { inputType: "number" },
    },
  ];

  // // 	const degradation: StartWithWithoutTierTwo = {
  // 	type: FieldType.SWW_T2,
  // 	inputName: "tiertwo.degradation_dry_matter_impacted_t2",
  // 	label: "module.dry_matter_impacted",
  // 	unit: "[%]",
  // 	links: [],
  // 	startProps: { inputType: "number" },
  // 	withoutProps: { inputType: "number" },
  // 	withProps: { inputType: "number" },
  // };

  return [
    {
      name: "",
      inputGroups: [dataSource, ...forestManagementRows],
    },
  ];
};

// const getT2OptionalSubmodules: () => OptionalSubmodule<DisturbanceT2 | LoggingT2>[] = () => {
const getT2OptionalSubmodules: () => OptionalSubmodule<any>[] = () => {
  const fractionAffected: StartWithWithoutTierTwo = {
    type: FieldType.SWW_T2,
    inputName: (index: number) =>
      `disturbances.${index}.dry_matter_impacted_t2`,
    label: "module.dry_matter_impacted",
    unit: "[%]",
    links: [],
    startProps: { inputType: "number" },
    withoutProps: { inputType: "number" },
    withProps: { inputType: "number" },
  };
  const startYearDisturbance: StartWithWithoutTierTwo = {
    type: FieldType.SWW_T2,
    inputName: (index: number) => `disturbances.${index}.start_year_t2`,
    label: "module.start_year_of_disturbance",
    unit: "[n. of years]",
    links: [],
    startProps: { inputType: "number" },
    withoutProps: { inputType: "number" },
    withProps: { inputType: "number" },
  };
  const startYearRotations: StartWithWithoutTierTwo = {
    type: FieldType.SWW_T2,
    inputName: (index: number) => "tiertwo.rotation_start_year_t2",
    label: "module.start_year_of_rotations",
    unit: "[n. of years]",
    links: [],
    startProps: { inputType: "number" },
    withoutProps: { inputType: "number" },
    withProps: { inputType: "number" },
  };

  const startYearLogging: StartWithWithoutTierTwo = {
    type: FieldType.SWW_T2,
    inputName: (index: number) => "tiertwo.logging_start_year_t2",
    label: "module.start_year_of_harvesting",
    unit: "[n. of years]",
    links: [],
    startProps: { inputType: "number" },
    withoutProps: { inputType: "number" },
    withProps: { inputType: "number" },
  };

  const logging: StartWithWithoutTierTwo = {
    type: FieldType.SWW_T2,
    inputName: "tiertwo.dryMatterLogged",
    label: "module.dry_matter_harvested",
    unit: "[t]",
    links: [],
    startProps: { inputType: "number" },
    withoutProps: { inputType: "number" },
    withProps: { inputType: "number" },
  };

  return [
    {
      name: "module.rotations",
      inputName: "tiertwo.rotations",
      inputGroups: [startYearRotations],
    },
    {
      name: "module.harvesting",
      inputName: "tiertwo.logging",
      inputGroups: [startYearLogging /*, logging*/],
    },
    {
      name: "module.disturbance",
      inputName: "disturbances",
      inputGroups: [startYearDisturbance /*fractionAffected*/],
    },
  ];
};

export const getSchemas = (isForestRemainsForest: boolean) => {
  const moduleSchema: ModuleSchema = {
    initInputGroups: getInitInputGroups(),
    mandatorySubmodules: getMandatorySubmodules(),
    optionalSubmodules: getOptionalSubmodules(),
  };

  const t2Schema: T2Schema = {
    submodules: getT2Submodules(isForestRemainsForest),
    optionalSubmodules: getT2OptionalSubmodules(),
  };

  return { moduleSchema, t2Schema };
};
